/**
 * 가입 동의 기록 — 개인정보보호법 §22.
 *
 * 필수/선택 동의를 분리해 받았다는 사실과 그 시점을 남긴다.
 * 동의를 "받았다"는 주장의 입증 책임은 사업자에게 있으므로, 화면에서 체크만 받고
 * 저장하지 않으면 사실상 기록이 없는 것과 같다.
 *
 * 저장 위치는 users/{uid}.consent — 다른 설정과 같은 문서다(별도 컬렉션 불필요).
 */

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { logger } from '@/lib/logger'

/**
 * 문서 버전 — 각 문서의 시행일이다. 개정하면 올리고 재동의를 받는다.
 *
 * 문서별로 나눠 둔 이유: 이전에는 `CONSENT_VERSION = '2026-05-01'` 하나로 뭉뚱그렸는데
 * 그 값은 **두 문서 어느 쪽과도 일치하지 않았다**(약관 2026-05-12 · 방침 2026-08-17).
 * 방침만 개정해도 약관 동의 기록까지 함께 낡은 것으로 보이고, 반대로 어느 버전에
 * 동의했는지는 어느 쪽도 알 수 없었다 — 입증 책임을 지는 기록으로서는 무효에 가깝다.
 *
 * 문서 페이지의 표시 날짜도 이 상수에서 렌더한다(`formatVersionDate`).
 * 문서와 기록이 한 소스에서 나와야 다시 어긋나지 않는다.
 */
export const TERMS_VERSION = '2026-05-12'
export const PRIVACY_VERSION = '2026-08-24'

/**
 * 영수증 사진 국외이전 동의 버전 — 개인정보보호법 §28조의8.
 *
 * 방침 동의와 **따로 받는다.** 국외이전은 별도 동의 사항이고, 영수증 사진은 기존에
 * 고지한 이전 항목("거래 내역 텍스트")과 다른 범주다 — 사진에는 카드 번호나 주소가
 * 함께 찍혀 있을 수 있다. 방침 문서 하나를 올려 뭉뚱그리면, 촬영 기능을 쓰지 않는
 * 사용자에게까지 이전에 동의한 것처럼 기록이 남는다.
 *
 * 그래서 **처음 촬영을 쓰려는 시점에** 그 자리에서 받는다. 쓰지 않는 사용자는
 * 동의 기록도 없고 전송도 없다.
 */
export const RECEIPT_TRANSFER_VERSION = '2026-08-24'

/** 'YYYY-MM-DD' → '2026년 8월 17일' */
export function formatVersionDate(version: string): string {
  const [y, m, d] = version.split('-')
  return `${y}년 ${Number(m)}월 ${Number(d)}일`
}

export interface SignupConsent {
  /** 서비스 약관 (필수) */
  terms: boolean
  /** 개인정보 처리방침 (필수) */
  privacy: boolean
  /** 혜택·소식 알림 수신 (선택, 정보통신망법 §50) */
  marketing: boolean
}

/** 저장되는 동의 기록 — 문서별로 어느 버전에 동의했는지 남는다 */
export interface ConsentRecord {
  terms: { agreed: boolean; version: string }
  privacy: { agreed: boolean; version: string }
  /** 선택 동의라 버전 개념이 없다 — 수신 여부만 */
  marketing: { agreed: boolean }
}

export function buildConsentRecord(consent: SignupConsent): ConsentRecord {
  return {
    terms: { agreed: consent.terms, version: TERMS_VERSION },
    privacy: { agreed: consent.privacy, version: PRIVACY_VERSION },
    marketing: { agreed: consent.marketing },
  }
}

/**
 * 동의 기록을 남긴다. 실패해도 가입 자체는 막지 않는다 —
 * 계정은 이미 만들어졌고, 여기서 throw하면 사용자는 로그인도 못 한 채 멈춘다.
 * 대신 에러를 로깅해 누락을 관측 가능하게 한다.
 *
 * 기존 사용자의 평면 구조(`{terms: true, version: '2026-05-01'}`) 기록은
 * 마이그레이션하지 않는다. 그때 실제로 무엇에 동의했는지 지금 알 수 없으므로,
 * 추측해서 덮어쓰면 기록을 고치는 게 아니라 **없애는** 것이 된다.
 * 재동의가 필요해지면 그때 새 구조로 다시 받는다.
 */
export async function recordSignupConsent(uid: string, consent: SignupConsent): Promise<void> {
  try {
    await setDoc(
      doc(db, 'users', uid),
      {
        consent: {
          ...buildConsentRecord(consent),
          consentedAt: serverTimestamp(),
        },
      },
      { merge: true }
    )
  } catch (error) {
    logger.error('Failed to record signup consent:', error)
  }
}

/**
 * 영수증 사진 국외이전 동의를 남긴다.
 *
 * 여기서는 실패를 삼키지 않는다. 가입 동의와 달리 이 동의는 **전송의 전제**이므로,
 * 기록에 실패했는데 전송이 일어나면 "동의 없이 이전"이 된다.
 */
export async function recordReceiptTransferConsent(uid: string): Promise<void> {
  await setDoc(
    doc(db, 'users', uid),
    {
      consent: {
        receiptTransfer: {
          agreed: true,
          version: RECEIPT_TRANSFER_VERSION,
          agreedAt: serverTimestamp(),
        },
      },
    },
    { merge: true }
  )
}

/**
 * 현재 버전에 동의했는지. 읽기에 실패하면 **false**를 돌려준다 —
 * 모르면 동의한 것으로 취급하지 않는다(전송이 걸린 판정이다).
 */
export async function hasReceiptTransferConsent(uid: string): Promise<boolean> {
  try {
    const snap = await getDoc(doc(db, 'users', uid))
    const record = snap.data()?.consent?.receiptTransfer as
      | { agreed?: boolean; version?: string }
      | undefined
    return record?.agreed === true && record.version === RECEIPT_TRANSFER_VERSION
  } catch (error) {
    logger.error('Failed to read receipt transfer consent:', error)
    return false
  }
}

/**
 * 방침 개정 고지 — 개인정보보호법 §22.
 *
 * ## 왜 "재동의"가 아니라 "고지"인가
 *
 * 이번 개정으로 늘어난 처리(영수증 사진 수집·국외이전)는 **촬영 기능을 처음 쓰는
 * 시점에 별도 동의**로 이미 막혀 있다(`RECEIPT_TRANSFER_VERSION`). 즉 고지를 보기만
 * 하고 아무것도 누르지 않은 사용자에게는 새 처리가 일어나지 않는다.
 *
 * 그래서 여기서 전체 사용자에게 다시 받아야 할 동의는 없고, 남는 의무는 **개정 사실을
 * 알리는 것**이다. 이 구분을 코드에도 남긴다 — 확인 클릭을 `consent.privacy`에 쓰면
 * "동의를 받았다"는 기록이 되어 버리는데, 사용자가 실제로 한 것은 확인뿐이다.
 * 그래서 별도 필드 `privacyNotice`에 남긴다.
 */
export const PRIVACY_CHANGE_SUMMARY = [
  '수집 항목에 "영수증 사진"을 추가했습니다 — 촬영 기능을 쓸 때만 해당합니다.',
  '영수증 사진의 국외이전(미국 Anthropic) 사실을 국외이전 표에 명시했습니다.',
] as const

export interface PrivacyNoticeState {
  /** 개정 고지를 보여줘야 하는가 */
  needed: boolean
  /** 마지막으로 동의한 방침 버전 — 알 수 없으면 null (구조 이전의 평면 기록 포함) */
  agreedVersion: string | null
}

/**
 * 고지가 필요한지 판정한다.
 *
 * 실패하면 `needed: false`다. 이 기본값은 `hasReceiptTransferConsent`와 **반대**인데,
 * 판정의 성격이 다르기 때문이다 — 그쪽은 모르면 전송을 막아야 하고(안전한 쪽이 거부),
 * 이쪽은 모른다고 배너를 띄우면 네트워크가 흔들릴 때마다 이미 확인한 사용자에게
 * 같은 고지가 반복된다(안전한 쪽이 침묵). 고지는 게이트가 아니라서 한 번 늦어도 된다.
 */
export async function getPrivacyNoticeState(uid: string): Promise<PrivacyNoticeState> {
  try {
    const snap = await getDoc(doc(db, 'users', uid))
    const consent = snap.data()?.consent as Record<string, unknown> | undefined

    // 이미 이번 개정을 확인함
    const ack = consent?.privacyNotice as { version?: string } | undefined
    if (ack?.version === PRIVACY_VERSION) return { needed: false, agreedVersion: null }

    const privacy = consent?.privacy as { version?: string } | undefined
    const agreedVersion = typeof privacy?.version === 'string' ? privacy.version : null

    // 현행 버전에 이미 동의했다면(가입이 개정 이후) 고지할 것이 없다
    if (agreedVersion === PRIVACY_VERSION) return { needed: false, agreedVersion }

    return { needed: true, agreedVersion }
  } catch (error) {
    logger.error('Failed to read privacy notice state:', error)
    return { needed: false, agreedVersion: null }
  }
}

/**
 * 고지를 확인했음을 남긴다. `consent.privacy`는 건드리지 않는다 —
 * 확인은 동의가 아니고, 여기서 버전을 올리면 받은 적 없는 동의가 기록된다.
 */
export async function acknowledgePrivacyNotice(uid: string): Promise<void> {
  await setDoc(
    doc(db, 'users', uid),
    {
      consent: {
        privacyNotice: {
          version: PRIVACY_VERSION,
          acknowledgedAt: serverTimestamp(),
        },
      },
    },
    { merge: true }
  )
}
